// based on:
// https://www.youtube.com/watch?v=ZKlXqrcBx88
// https://github.com/jherr/fast-react-context

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useSyncExternalStore,
} from 'react';

export function createStore<Store>() {
  const useStoreData = () => {
    const store = useRef({});
    const subscribers = useRef(new Set<() => void>());

    const subscribe = useCallback((callback: () => void) => {
      subscribers.current.add(callback);
      return () => subscribers.current.delete(callback);
    }, []);
    const notify = useCallback(() => {
      subscribers.current.forEach((callback) => callback());
    }, []);
    const get = useCallback(() => store.current, []);
    const set = useCallback((value: Partial<Store>) => {
      if (Object.keys(value).length > 0) {
        store.current = { ...store.current, ...value };
      } else {
        store.current = value;
      }
    }, []);

    return {
      subscribe,
      notify,
      get,
      set,
    };
  };
  type UseStoreDataReturnType = ReturnType<typeof useStoreData>;

  const StoreContext = createContext<UseStoreDataReturnType | null>(null);

  const StoreProvider = ({ children }: { children: ReactNode }) => {
    return (
      <StoreContext.Provider value={useStoreData()}>
        {children}
      </StoreContext.Provider>
    );
  };

  const getDataWithSelector = (obj: Partial<Store>, selector: string) => {
    const keys = selector.split('.');

    for (let i = 0; i < keys.length; i++) {
      if (!obj.hasOwnProperty(keys[i])) {
        throw Error(`Store object has no property '${keys[i]}'`);
      }
      obj = obj[keys[i] as keyof Store] as Store;
    }

    return obj;
  };

  const setDataWithSelector = (
    obj: Partial<Store>,
    selector: string,
    value: any
  ) => {
    const keys = selector.split('.');
    let target = obj;

    for (let i = 0; i < keys.length - 1; i++) {
      if (!obj.hasOwnProperty(keys[i])) {
        throw Error(`Store object has no property '${keys[i]}'`);
      }
      target =
        target[keys[i] as keyof Store] &&
        (target[keys[i] as keyof Store] as Store);
    }
    target[keys[keys.length - 1] as keyof Store] = value;

    return target;
  };

  function useStore<T = any>(
    settings: { selector?: string; observe?: boolean } = {}
  ): [T, (callback: Partial<T> | ((prev: T) => Partial<T>)) => void] {
    const store = useContext(StoreContext)!;
    if (!store)
      throw new Error('Store not found. Check if you use it inside Provider');

    settings = Object.assign({ selector: '', observe: true }, settings);

    const state = settings.observe
      ? useSyncExternalStore(
          store.subscribe,
          settings.selector
            ? () => getDataWithSelector(store.get(), settings.selector || '')
            : store.get
        )
      : settings.selector
      ? () => getDataWithSelector(store.get(), settings.selector || '')
      : store.get();

    const setState = (callback: Partial<T> | ((prev: T) => Partial<T>)) => {
      const newState = settings.selector
        ? setDataWithSelector(
            store.get(),
            settings.selector,
            typeof callback === 'function'
              ? (callback as (prev: Partial<Store>) => T)(
                  getDataWithSelector(store.get(), settings.selector)
                )
              : callback
          )
        : callback;

      store.set(newState as Partial<Store>);
      store.notify();
    };

    return [state as T, setState];
  }

  return [StoreProvider, useStore] as const;
}
